/**
 * Public Signal Draft Generator V1 - Forbidden Term Scanner
 * 
 * Scans text for forbidden patterns that should not appear in public drafts.
 * Similar to BuilderSuggestionScopeGuard / HardmodeBoundaryGuard patterns.
 * 
 * BOUNDARIES:
 * - Advisory only: no execution authority
 * - Deterministic output only
 * - Fail closed on forbidden exposure patterns
 * - No mutation of inputs
 */

import {
  ForbiddenPatternMatch,
} from './types';

/**
 * Forbidden patterns that indicate internal system context
 * These should not appear in public-facing drafts
 */
const FORBIDDEN_PATTERNS = [
  // Internal modules and components
  { pattern: /ratchet/gi, category: 'governance' as const },
  { pattern: /admissibility/gi, category: 'governance' as const },
  { pattern: /receipt/gi, category: 'receipt' as const },
  { pattern: /execution[\s-]dispatcher/gi, category: 'governance' as const },
  { pattern: /autonomousLoop/gi, category: 'governance' as const },
  { pattern: /proposal[\s-]restoration/gi, category: 'governance' as const },
  { pattern: /closeout\s*artifact/gi, category: 'governance' as const },
  { pattern: /governance\s*pipeline/gi, category: 'governance' as const },
  { pattern: /intent\s*path/gi, category: 'governance' as const },
  { pattern: /pivot\s*decision/gi, category: 'governance' as const },
  { pattern: /strategy\s*simulation/gi, category: 'governance' as const },
  { pattern: /approval\s*decision/gi, category: 'governance' as const },
  { pattern: /context\s*reconciliation/gi, category: 'governance' as const },
  { pattern: /approval\s*flow/gi, category: 'governance' as const },
  { pattern: /surfaceRoute/gi, category: 'governance' as const },
  { pattern: /lane\s*coordination/gi, category: 'governance' as const },
  { pattern: /hardmode/gi, category: 'governance' as const },
  
  // Internal file paths
  { pattern: /src\/trustgate/gi, category: 'path' as const },
  { pattern: /artifacts\//gi, category: 'path' as const },
  { pattern: /tasks\/feat\//gi, category: 'path' as const },
  { pattern: /tasks\/fix\//gi, category: 'path' as const },
  { pattern: /docs\/evolution\//gi, category: 'path' as const },
  { pattern: /\.windsurf\//gi, category: 'path' as const },
  
  // Hash patterns (SHA-256, MD5, etc.)
  { pattern: /[a-f0-9]{64}/gi, category: 'hash' as const }, // SHA-256
  { pattern: /[a-f0-9]{32}/gi, category: 'hash' as const }, // MD5
  
  // IP-sensitive / patent-related terms
  { pattern: /patent[\s-]pending/gi, category: 'ip_sensitive' as const },
  { pattern: /patent[\s-]application/gi, category: 'ip_sensitive' as const },
  { pattern: /proprietary\s*algorithm/gi, category: 'ip_sensitive' as const },
  { pattern: /trade\s*secret/gi, category: 'ip_sensitive' as const },
  
  // Private repo names (unless explicitly allowed)
  { pattern: /SCRTHodl/gi, category: 'repo_name' as const },
  { pattern: /ws-trustgate/gi, category: 'repo_name' as const },
];

/**
 * Scan text for forbidden patterns
 */
export function scanForbiddenTerms(text: string): ForbiddenPatternMatch[] {
  const matches: ForbiddenPatternMatch[] = [];

  for (const { pattern, category } of FORBIDDEN_PATTERNS) {
    let match;
    const regex = new RegExp(pattern.source, pattern.flags);
    
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        pattern: pattern.source,
        matchedText: match[0],
        category,
      });
    }
  }

  return matches;
}

/**
 * Check if text contains any forbidden patterns
 */
export function containsForbiddenTerms(text: string): boolean {
  return scanForbiddenTerms(text).length > 0;
}

/**
 * Get forbidden patterns by category
 */
export function getForbiddenPatternsByCategory(
  text: string,
  category: ForbiddenPatternMatch['category']
): ForbiddenPatternMatch[] {
  return scanForbiddenTerms(text).filter(m => m.category === category);
}

/**
 * Check if text is safe for public disclosure
 */
export function isTextSafeForPublic(text: string): boolean {
  return !containsForbiddenTerms(text);
}
